import type { Audience, DemoRoute, GuideState, GuideStep } from "./types.js";

export type GuideEvent =
  | "brand-created"
  | "invite-opened"
  | "guest-joined"
  | "photo-sent"
  | "wall-seen"
  | "photo-hidden"
  | "album-seen";

export type Role = "cerimonial" | "guest" | "wall" | "client";

export interface StepCopy {
  readonly step: GuideStep;
  readonly role: Role;
  readonly route: DemoRoute;
  readonly hint: string;
  readonly text: string;
  readonly go: string;
  readonly trigger: GuideEvent | null;
  readonly next: string | null;
  readonly done?: string;
  readonly doneText?: string;
}

export const STEPS: readonly StepCopy[] = [
  {
    step: "brand",
    role: "cerimonial",
    route: "marca",
    hint: "Preencha o nome do cerimonial e toque em Criar evento.",
    text: "Comece pelo seu cerimonial. É assim que o seu cliente vai ver o evento.",
    go: "Montar a marca",
    trigger: "brand-created",
    next: null,
  },
  {
    step: "panel",
    role: "cerimonial",
    route: "painel",
    hint: "Toque em Convite para ver o que o convidado vê.",
    text: "O evento está pronto e já está ao vivo. O QR vai no convite, no cartão de mesa e no telão.",
    go: "Abrir o painel",
    trigger: "invite-opened",
    next: "Ver o que o convidado vê",
  },
  {
    step: "invite",
    role: "guest",
    route: "convite",
    hint: "Digite um nome e toque em Entrar no evento.",
    text: "Agora você é um convidado. Ele só digita o nome e o sobrenome. Não instala nada.",
    go: "Abrir o convite",
    trigger: "guest-joined",
    next: null,
  },
  {
    step: "camera",
    role: "guest",
    route: "camera",
    hint: "Tire uma foto ou escolha uma do casamento.",
    text: "Cada convidado tem 10 fotos. Assim que ele envia, a foto vai para o álbum e para o telão.",
    go: "Abrir a câmera",
    trigger: "photo-sent",
    next: null,
  },
  {
    step: "wall",
    role: "wall",
    route: "telao",
    hint: "Veja sua foto em destaque e toque em Próximo.",
    text: "No evento, o telão abre no computador ligado à TV e roda sozinho, com o nome de quem tirou cada foto.",
    go: "Ver o telão",
    trigger: "wall-seen",
    next: "Próximo",
  },
  {
    step: "moderation",
    role: "cerimonial",
    route: "moderacao",
    hint: "Toque em Ocultar na foto tremida e veja o telão acima.",
    text: "Você controla o que aparece. A foto oculta sai do telão na hora.",
    go: "Ir para a moderação",
    trigger: "photo-hidden",
    next: null,
    done: "Pronto: a foto saiu do telão na hora.",
    doneText: "Ela também sai do álbum. Agora veja o álbum que o seu cliente recebe.",
  },
  {
    step: "album",
    role: "client",
    route: "album",
    hint: "Veja o álbum que o seu cliente recebe e toque em Concluir.",
    text: "Depois do evento, as fotos ficam separadas por horário, com a sua marca, prontas para entregar. Toque em 22h ou 23h para filtrar.",
    go: "Abrir o álbum",
    trigger: "album-seen",
    next: "Concluir",
  },
  {
    step: "done",
    role: "client",
    route: "fim",
    hint: "Fale com a gente no WhatsApp.",
    text: "É isso que o seu cliente recebe. Quer usar no seu próximo evento?",
    go: "Ver o resumo",
    trigger: null,
    next: null,
  },
];

export const CLIENT_STEPS: readonly StepCopy[] = [
  {
    step: "invite",
    role: "guest",
    route: "convite",
    hint: "Digite um nome e toque em Entrar no evento.",
    text: "É assim que os seus convidados entram: só o nome e o sobrenome, sem instalar nada.",
    go: "Abrir o convite",
    trigger: "guest-joined",
    next: null,
  },
  {
    step: "camera",
    role: "guest",
    route: "camera",
    hint: "Tire uma foto ou escolha uma do casamento.",
    text: "Cada convidado tem 10 fotos. Assim que envia, a foto vai para o álbum e para o telão.",
    go: "Abrir a câmera",
    trigger: "photo-sent",
    next: null,
  },
  {
    step: "wall",
    role: "wall",
    route: "telao",
    hint: "Veja a foto em destaque e toque em Próximo.",
    text: "No dia, o telão roda numa TV do salão, com o nome de quem tirou cada foto.",
    go: "Ver o telão",
    trigger: "wall-seen",
    next: "Próximo",
  },
  {
    step: "album",
    role: "client",
    route: "album",
    hint: "Veja o seu álbum e toque em Concluir.",
    text: "No dia seguinte, as fotos estão no álbum, separadas por horário e em qualidade original. Toque em 22h ou 23h para filtrar.",
    go: "Abrir o álbum",
    trigger: "album-seen",
    next: "Concluir",
  },
  {
    step: "done",
    role: "client",
    route: "fim",
    hint: "Gostou? Fale com o seu cerimonial.",
    text: "É assim que fica o seu evento.",
    go: "Ver o resumo",
    trigger: null,
    next: null,
  },
];

export function stepsFor(audience: Audience): readonly StepCopy[] {
  return audience === "client" ? CLIENT_STEPS : STEPS;
}

export function totalSteps(audience: Audience): number {
  return stepsFor(audience).filter((copy) => copy.step !== "done").length;
}

export const TOTAL_STEPS = totalSteps("planner");

const ROLE_LABELS: Record<Audience, Record<Role, string>> = {
  planner: {
    cerimonial: "Você, cerimonial",
    guest: "Convidado",
    wall: "Telão",
    client: "Seu cliente",
  },
  client: {
    cerimonial: "Seu cerimonial",
    guest: "Seu convidado",
    wall: "Telão",
    client: "Você",
  },
};

const ROLE_SHIFTS: Record<Audience, Record<Role, string>> = {
  planner: {
    cerimonial: "De volta ao seu painel",
    guest: "Agora você é o convidado",
    wall: "Agora você vê o telão",
    client: "Agora você é o seu cliente",
  },
  client: {
    cerimonial: "O painel do seu cerimonial",
    guest: "Agora você é um convidado",
    wall: "Agora você vê o telão",
    client: "Agora é o seu álbum",
  },
};

export const ROLE_LABEL = ROLE_LABELS.planner;

export const ROLE_SHIFT = ROLE_SHIFTS.planner;

export function roleLabel(role: Role, audience: Audience): string {
  return ROLE_LABELS[audience][role];
}

export function roleShift(role: Role, audience: Audience): string {
  return ROLE_SHIFTS[audience][role];
}

export function stepCopy(step: GuideStep, audience: Audience = "planner"): StepCopy {
  const steps = stepsFor(audience);
  return steps.find((copy) => copy.step === step) ?? (steps[0] as StepCopy);
}

export function stepNumber(step: GuideStep, audience: Audience = "planner"): number {
  const steps = stepsFor(audience);
  return Math.min(steps.findIndex((copy) => copy.step === step) + 1, totalSteps(audience));
}

export function previousStep(step: GuideStep, audience: Audience = "planner"): StepCopy | null {
  const steps = stepsFor(audience);
  return steps[steps.findIndex((copy) => copy.step === step) - 1] ?? null;
}

export function advance(
  guide: GuideState,
  event: GuideEvent,
  audience: Audience = "planner",
): GuideState {
  const steps = stepsFor(audience);
  const index = steps.findIndex((copy) => copy.step === guide.step);
  const current = steps[index];
  const following = steps[index + 1];
  if (!current || !following || current.trigger !== event) {
    return guide;
  }
  return { ...guide, step: following.step };
}

const ROUTE_ROLE: Record<DemoRoute, Role> = {
  inicio: "cerimonial",
  marca: "cerimonial",
  painel: "cerimonial",
  moderacao: "cerimonial",
  convite: "guest",
  camera: "guest",
  galeria: "guest",
  album: "guest",
  telao: "wall",
  fim: "client",
};

export function roleForRoute(route: DemoRoute, step: GuideStep): Role {
  if (route === "album" && (step === "album" || step === "done")) {
    return "client";
  }
  return ROUTE_ROLE[route];
}
